// src/pages/SubscriptionPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  useNavigate,
  Link,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { motion } from "framer-motion";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaTimesCircle, FaBolt, FaLock } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper/modules";
import "../styles/SubscriptionPage.css";
// import { Target } from "lucide-react";
// import { useParams } from "react-router-dom";

export default function SubscriptionPage() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  // const PDF_URL = process.env.REACT_APP_API_BASE_URL_PDF;
  const { productId } = useParams(); // gets productId from URL
  const [searchParams] = useSearchParams();
  const mp_id = searchParams.get("mp_id");
  const sp_id = searchParams.get("sp_id");
  const navigate = useNavigate();
  // const { state } = useLocation();

  // STATES
  const [magDetails, setMagDetails] = useState(null);
  const [recentMagazines, setRecentMagazines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loadingMagazines, setLoadingMagazines] = useState(true);
  // const [loadingDetails, setLoadingDetails] = useState(true);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  // const [appreciationMsg, setAppreciationMsg] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  // Page loader
  const PageLoader = ({ text = "Loading Magazine Details..." }) => {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#ffffff",
          zIndex: 99999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <div
          className="spinner-border"
          role="status"
          style={{ width: "3.5rem", height: "3.5rem", color: "#f64a1aff" }}
        />
        <p className="mt-3 fw-semibold">{text}</p>
      </div>
    );
  };

  // FETCH MAGAZINE DETAILS
  useEffect(() => {
    if (!productId) return;

    const fetchMagazineDetails = async () => {
      setPageLoading(true); // start loader
      try {
        const res = await fetch(`${API_BASE_URL}magazine_detail.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mag_id: productId }),
        });

        const data = await res.json();
        console.log("Magazine Detail API:", data);

        if (data?.head?.code === 200) {
          // Check if body is an array or object
          const apiMag = Array.isArray(data.body) ? data.body[0] : data.body;
          const subPlan = data.subscription_plan; // Extracting the separate plan object

          if (apiMag) {
            setMagDetails({
              id: productId,
              mpid: mp_id,
              spid: sp_id,
              code: apiMag.magazine_code,
              title: apiMag.magazine_name,
              publish_date: apiMag.publish_date,
              tax: apiMag.tax,
              discount: apiMag.discount,
              description: apiMag.short_desc,
              full_desc: apiMag.full_desc,
              img: apiMag.cover_image, // Base64 or URL
              single_price: apiMag.single_plan,
              yearly_price: apiMag.year_plan,
              // Map dynamic monthly/yearly prices from the API
              monthly_sub: subPlan?.monthly_plan_price || "399",
              yearly_sub: subPlan?.yearly_plan_price || "3,999",
              stock: apiMag.stock || 0, // <-- stock value from API
            });
          }
        } else {
          setMagDetails(null);
        }
      } catch (err) {
        console.error("Magazine details error:", err);
        setMagDetails(null);
      } finally {
        // setLoadingDetails(false);
        setPageLoading(false); // stop loader
      }
    };

    fetchMagazineDetails();
  }, [API_BASE_URL, productId, mp_id, sp_id]);

  // check subscription status
  // useEffect(() => {
  //   const checkSubscription = async () => {
  //     setPageLoading(true); // start loader
  //     const user = JSON.parse(localStorage.getItem("user"));
  //     if (!user?.user_id || !magDetails?.id) return;

  //     try {
  //       const res = await fetch(`${API_BASE_URL}check_subscription.php`, {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           user_id: user.user_id,
  //         }),
  //       });

  //       const data = await res.json();
  //       console.log("USER:", user);
  //       console.log("MAG ID:", magDetails.id);
  //       console.log("Check Subscription API:", data);

  //       if (data?.head?.code === 200 && Array.isArray(data.body)) {
  //         const subscription = data.body.find(
  //           (item) =>
  //             item.type === "Subscription" &&
  //             Number(item.pid) === Number(magDetails.id) &&
  //             item.order_status !== "Cancelled"
  //         );

  //         if (subscription) {
  //           const today = new Date();
  //           const endDate = new Date(subscription.end_date);

  //           if (today <= endDate) {
  //             setIsSubscribed(true);
  //             setSubscriptionEndDate(subscription.end_date);
  //             return;
  //           }
  //         }
  //       }

  //       setIsSubscribed(false);
  //     } catch (err) {
  //       console.error("Subscription check failed", err);
  //       setIsSubscribed(false);
  //     }
  //     finally{
  //       setPageLoading(false); // start loader
  //     }
  //   };

  //   checkSubscription();
  // }, [magDetails, API_BASE_URL]);
  useEffect(() => {
    const checkSubscription = async () => {
      setPageLoading(true);

      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?.user_id || !magDetails?.publish_date) {
        setPageLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}check_subscription.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.user_id }),
        });

        const data = await res.json();
        console.log("Check Subscription API:", data);

        if (data?.head?.code !== 200 || !Array.isArray(data.body)) {
          setIsSubscribed(false);
          return;
        }

        const today = new Date();
        const magPublishDate = new Date(magDetails.publish_date);

        /* =====================================================
         ✅ STEP 1: YEARLY SUBSCRIPTION (GLOBAL ACCESS)
      ===================================================== */
        const yearlySub = data.body.find((item) => {
          const planType = (item.plan_type || "").toLowerCase();
          return (
            item.type === "Subscription" &&
            planType.includes("year") &&
            item.order_status === "Accepted"
          );
        });

        if (yearlySub) {
          const endDate = new Date(yearlySub.end_date);

          if (today <= endDate && magPublishDate <= endDate) {
            setIsSubscribed(true);
            setSubscriptionEndDate(yearlySub.end_date);
            return;
          }
        }

        /* =====================================================
         ✅ STEP 2: MAGAZINE-SPECIFIC SUBSCRIPTION (MONTHLY)
      ===================================================== */
        const magSpecificSub = data.body.find(
          (item) =>
            item.type === "Subscription" &&
            Number(item.pid) === Number(magDetails.id) &&
            item.order_status === "Accepted",
        );

        if (magSpecificSub) {
          const endDate = new Date(magSpecificSub.end_date);
          if (today <= endDate) {
            setIsSubscribed(true);
            setSubscriptionEndDate(magSpecificSub.end_date);
            return;
          }
        }

        setIsSubscribed(false);
      } catch (err) {
        console.error("Subscription check failed", err);
        setIsSubscribed(false);
      } finally {
        setPageLoading(false);
      }
    };

    checkSubscription();
  }, [magDetails, API_BASE_URL]);

  // FETCH RECENT MAGAZINES
  useEffect(() => {
    const fetchRecentMagazines = async () => {
      setPageLoading(true); // start loader
      try {
        const res = await fetch(`${API_BASE_URL}recent_magazines_list.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (data?.head?.code === 200) {
          setRecentMagazines(data.body);
        } else {
          setRecentMagazines([]);
        }
      } catch (err) {
        console.error("Recent magazines API error:", err);
        setRecentMagazines([]);
      } finally {
        setLoadingMagazines(false);
        setPageLoading(false); // start loader
      }
    };

    fetchRecentMagazines();
  }, [API_BASE_URL]);

  // FETCH POPULAR CATEGORIES
  useEffect(() => {
    const fetchCategories = async () => {
      setPageLoading(true); // start loader
      try {
        const response = await fetch(`${API_BASE_URL}subcategory_list.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        const data = await response.json();
        console.log("API Response:", data);

        if (data.head.code === 200 && data.body.length > 0) {
          // Sort the raw API data by subcategory_rank first
          const sortedRaw = data.body.sort((a, b) => {
            const rankA = Number(a.subcategory_rank) || 0; // ensure number
            const rankB = Number(b.subcategory_rank) || 0;
            return rankA - rankB; // ascending order
          });

          // Map to simplified objects after sorting
          const sortedCategories = sortedRaw.map((item) => ({
            name: item.subcategory_name,
            img: item.subcategory_image,
            mp_id: item.mp_id,
            sp_id: item.sp_id,
          }));

          console.log("Sorted Categories:", sortedCategories);

          setCategories(sortedCategories);
        } else {
          console.warn("No categories found:", data.head.msg);
          setCategories([]);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
        setPageLoading(false); // start loader
      }
    };

    fetchCategories();
  }, [API_BASE_URL]);

  // FORMAT RECENT MAGAZINES DATES
  const formattedMagazines = useMemo(() => {
    const months = [
      "January",
      "Febuary",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return recentMagazines.map((mag) => {
      const date = new Date(mag.publish_date);
      return {
        ...mag,
        formattedDate: !isNaN(date)
          ? `${months[date.getMonth()]} ${date.getFullYear()}`
          : "",
      };
    });
  }, [recentMagazines]);

  // TOAST MESSAGE
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  // ADD TO CART
  const dispatchCartUpdated = () => {
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleAddToCart = () => {
    if (!selectedPlan || !magDetails) return;

    const currentStock = Number(magDetails.stock) || 0;
    const isSubscription = selectedPlan.type === "Subscription";
    const isYearlyHardcopy =
      selectedPlan.type === "Hardcopy" &&
      selectedPlan.title?.toLowerCase() === "yearly";

    // 1. Stock Validation (ONLY for Hardcopy)
    // Subscriptions skip this check entirely
    if (!isSubscription && currentStock <= 0) {
      showToastMessage("Physical copies are currently out of stock! ⚠️");
      return;
    }

    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const cartId = `magazine-${magDetails.id}-${magDetails.mpid}-${magDetails.spid}-${selectedPlan.title}-${selectedPlan.type}`;
    const existingIndex = cart.findIndex((item) => item.id === cartId);

    // 2. Logic for "Add-Only-Once" items (Subscriptions or Yearly Hardcopy)
    if (isSubscription || isYearlyHardcopy) {
      if (existingIndex !== -1) {
        showToastMessage(
          isSubscription
            ? "Subscription already added to cart 🛒"
            : "Yearly hard copy already added to cart 🛒",
        );
        return;
      }

      cart.push({
        id: cartId,
        code: magDetails.code,
        mpid: magDetails.mpid,
        spid: magDetails.spid,
        product_id: magDetails.id,
        title: magDetails.title,
        image: magDetails.img || magDetails.product_url || "",
        price: selectedPlan.price
          ? Number(selectedPlan.price.replace(/[₹,]/g, ""))
          : 199,
        quantity: 1,
        availableStock: isSubscription ? 999 : currentStock, // Subscriptions effectively have infinite stock
        plan: selectedPlan.title,
        type: selectedPlan.type,
        tax: magDetails.tax,
      });

      localStorage.setItem("cart", JSON.stringify(cart));
      dispatchCartUpdated();
      showToastMessage(
        isSubscription ? "Subscription added! 🛒" : "Yearly copy added! 🛒",
      );
      return;
    }

    // 3. Logic for Normal Hard Copies (Incremental Quantity)
    if (existingIndex !== -1) {
      // Check if adding one more exceeds stock
      if (cart[existingIndex].quantity + 1 > currentStock) {
        showToastMessage(`Only ${currentStock} physical items available! ⚠️`);
        return;
      }
      cart[existingIndex].quantity += 1;
      showToastMessage(`Increased quantity of ${magDetails.title} 🛒`);
    } else {
      cart.push({
        id: cartId,
        code: magDetails.code,
        mpid: magDetails.mpid,
        spid: magDetails.spid,
        product_id: magDetails.id,
        title: magDetails.title,
        image: magDetails.img || magDetails.product_url || "",
        price: selectedPlan.price
          ? Number(selectedPlan.price.replace(/[₹,]/g, ""))
          : 199,
        quantity: 1,
        availableStock: currentStock,
        plan: selectedPlan.title,
        type: selectedPlan.type,
        tax: magDetails.tax,
      });
      showToastMessage(`${magDetails.title} added to cart! 🛒`);
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    dispatchCartUpdated();
  };

  const handleCategoryClick = ({ mp_id, sp_id }) => {
    navigate(`/magazines?mp_id=${mp_id}&sp_id=${sp_id}`);
  };

  if (pageLoading) {
    return <PageLoader text="Loading magazine details..." />;
  }

  if (!magDetails) {
    return (
      <div className="container-fluid text-center mt-5">
        <h3>No Magazine Selected</h3>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  // if (loadingDetails) {
  //   return <div className="text-center mt-5">Loading magazine details...</div>;
  // }

  // if (!magDetails) {
  //   return (
  //     <div className="container-fluid text-center mt-5">
  //       <h3>No Magazine Selected</h3>
  //       <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
  //         Go Back
  //       </button>
  //     </div>
  //   );
  // }
  const openPdfInNewTab = () => {
    navigate(`/pdf-viewer/${magDetails.id}`, {
      state: { mag_id: magDetails.id },
    });
  };

  // format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d)) return "";

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const formatDateMonthYear = (dateStr) => {
    if (!dateStr) return "";

    const d = new Date(dateStr.replace(" ", "T"));
    if (isNaN(d)) return "";

    const month = d.toLocaleString("en-US", { month: "long" });
    const year = d.getFullYear();

    return `${month} ${year}`;
  };

  // discount calculation for hardcopy

  const yearlyPrice = Number(
    magDetails.year_plan || magDetails.yearly_price || 0,
  );
  const discountPercent = Number(magDetails.discount || 0);

  const discountAmount =
    discountPercent > 0 ? Math.round((yearlyPrice * discountPercent) / 100) : 0;

  const discountedYearlyPrice =
    discountPercent > 0 ? yearlyPrice - discountAmount : yearlyPrice;

  // // discount calculation for subscription
  // const subYearlyPrice = Number(magDetails.subscription_plan?.yearly_plan_price || 0);
  // const subDiscountPercent = Number(magDetails.subscription_plan?.discount_percent || 0);

  // const subDiscountAmount =
  //   subDiscountPercent > 0
  //     ? Math.round((subYearlyPrice * subDiscountPercent) / 100)
  //     : 0;

  // const subDiscountedYearlyPrice =
  //   subDiscountPercent > 0
  //     ? subYearlyPrice - subDiscountAmount
  //     : subYearlyPrice;

  return (
    <>
      {" "}
      {pageLoading && <PageLoader />}
      <div className="container-fluid subscriptionpage-container">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="row shadow-sm p-1 mt-3 rounded"
          style={{ background: "#fff" }}
        >
          {/* LEFT SIDE: IMAGE */}
          <div className="col-12 col-md-5 col-lg-4 text-center mb-4 mb-md-0 mt-3 d-flex flex-column align-items-center">
            <img
              src={magDetails.img || magDetails.product_url}
              alt={magDetails.title}
              className="shadow-sm"
              style={{ width: "100%", maxWidth: "460px", borderRadius: "10px" }}
            />
            <button
              className="btn btn-outline-primary form-control mt-3 addbtn"
              onClick={() =>
                navigate("/preview", {
                  state: {
                    mag: { mag_id: magDetails.id, title: magDetails.title },
                  },
                })
              }
            >
              Preview
            </button>
          </div>

          {/* RIGHT SIDE: DETAILS */}
          <div className="col-12 col-md-7 col-lg-8 mt-3 mx-auto">
            <div
              className="p-3 shadow-sm rounded"
              style={{
                background: "#ffffff",
                borderRadius: "15px",
                border: "1px solid #f0f0f0",
              }}
            >
              <div
                className="d-flex flex-wrap align-items-center"
                style={{ gap: "10px" }}
              >
                <h2
                  className="fw-bold m-0"
                  style={{ fontSize: "1.6rem", whiteSpace: "wrap" }}
                >
                  {magDetails.title}
                </h2>
                <p
                  className="text-muted m-0"
                  style={{ fontSize: "0.95rem", whiteSpace: "nowrap" }}
                >
                  Published:{" "}
                  <strong>
                    {formatDateMonthYear(magDetails.publish_date) || "Aug 2025"}
                  </strong>
                </p>
                {/* Share Button */}
                <span
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: magDetails.title,
                        text: `Check out this magazine: ${magDetails.title}`,
                        url: window.location.href,
                      });
                    } else {
                      alert("Sharing not supported on this device.");
                    }
                  }}
                  className="bi bi-share-fill"
                  style={{ cursor: "pointer", fontSize: "1.1rem" }}
                  title="Share"
                ></span>
              </div>

              <hr className="my-3" />
              <div className="shortdesc">
                <p className="text-muted">{magDetails.description}</p>
              </div>

              {isSubscribed ? (
                /* ================= SUBSCRIBED ================= */
                <section className="mt-3">
                  <button
                    className="btn btn-success fw-semibold"
                    onClick={openPdfInNewTab}
                  >
                    READ MAGAZINE
                  </button>

                  <div className="text-muted mt-2">
                    Subscription valid till: {formatDate(subscriptionEndDate)}
                  </div>
                </section>
              ) : (
                /* ================= NOT SUBSCRIBED ================= */
                <section>
                  <h5 className="fw-semibold mb-3 text-start">
                    Digital Subscription
                  </h5>

                  <span className="text-success">
                    (To read magazine only online)
                  </span>

                  <div className="d-flex flex-wrap gap-3 mt-2 align-items-center justify-content-center">
                    {/* Monthly */}
                    <div
                      className={`plan-btn ${
                        selectedPlan?.title === "Monthly" ? "active" : ""
                      }`}
                      onClick={() =>
                        setSelectedPlan({
                          title: "Monthly",
                          price: `₹${magDetails.monthly_sub}`,
                          duration: "/month",
                          type: "Subscription",
                        })
                      }
                    >
                      <span className="plan-title">1 Month</span>
                      <span className="plan-price">
                        ₹{magDetails.monthly_sub}
                      </span>
                    </div>

                    {/* Yearly */}
                    <div
                      className={`plan-btn ${
                        selectedPlan?.title === "Yearly sub" ? "active" : ""
                      }`}
                      onClick={() =>
                        setSelectedPlan({
                          title: "Yearly sub",
                          price: `₹${magDetails.yearly_sub}`,
                          duration: "/year",
                          type: "Subscription",
                        })
                      }
                    >
                      <span className="plan-title">1 Year</span>
                      <span className="plan-price">
                        ₹{magDetails.yearly_sub}
                      </span>
                      <span className="sub-per-month">
                        ₹
                        {(
                          Number(magDetails.yearly_sub.replace(/,/g, "")) / 12
                        ).toFixed(0)}
                        /month
                      </span>
                    </div>

                    {/* Subscribe button */}
                    <button
                      className="
        btn btn-warning fw-semibold
        mx-auto mx-lg-0
        ms-lg-auto
      "
                      onClick={handleAddToCart}
                      disabled={
                        !selectedPlan || selectedPlan.type !== "Subscription"
                      }
                    >
                      SUBSCRIBE
                    </button>
                  </div>
                </section>
              )}

              <div className="d-flex justify-content-evenly mt-3 flex-wrap text-center">
                <div className="d-flex align-items-center gap-2">
                  <FaTimesCircle style={{ color: "#ff4d4f" }} />
                  <p className="m-0">Cancel within 4hrs</p>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2 mx-2">
                  <FaBolt style={{ color: "#1890ff" }} />
                  <p className="m-0">Digital Subscription Instant Access</p>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2 mx-2">
                  <FaLock style={{ color: "#52c41a" }} />
                  <p className="m-0">Verified Secure Payment</p>
                </div>
              </div>

              {/* OR Divider */}
              <div className="or-divider">
                <hr />
                <span className="or-text">(OR)</span>
              </div>

              <section>
                <h5 className="fw-semibold mb-3 text-start">
                  Choose Your Plan
                </h5>

                <span className="text-success">
                  (To get hard copy of the Magazine)
                </span>

                <div className="d-flex flex-wrap gap-3 mt-2 align-items-center justify-content-center">
                  {/* Yearly */}
                  <div className="d-flex flex-column align-items-start">
                    <button
                      className={`plan-btn ${
                        selectedPlan?.title === "Yearly" ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedPlan({
                          title: "Yearly",
                          price: `₹${discountedYearlyPrice}`,
                          duration: "/year",
                          type: "Hardcopy",
                        });
                      }}
                    >
                      {/* ROW: Title + Original Price */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="plan-title">1 Year</span>

                        {discountPercent > 0 && (
                          <span
                            className="plan-price"
                            style={{
                              textDecoration: "line-through",
                              color: "#dc3545",
                              fontSize: "0.9rem",
                            }}
                          >
                            ₹{yearlyPrice}
                          </span>
                        )}
                      </div>

                      {/* Final Price (next line) */}
                      <span className="plan-price fw-bold">
                        ₹{discountedYearlyPrice.toFixed(2)}
                      </span>
                    </button>
                    <div className="d-flex flex-row-reverse">
                      {/* SAVE TEXT OUTSIDE */}
                      {discountPercent > 0 && (
                        <span className="text-success fw-semibold small mt-1">
                          Save ₹ {discountAmount}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* One-time */}
                  <div className="mb-4">
                    <button
                      className={`plan-btn ${
                        selectedPlan?.title === "Onetime" ? "active" : ""
                      }`}
                      onClick={() =>
                        setSelectedPlan({
                          title: "Onetime",
                          price: `₹${magDetails.single_price}`,
                          duration: "",
                          type: "Hardcopy",
                        })
                      }
                    >
                      <span className="plan-title">Buy this issue</span>
                      <span className="plan-price">
                        ₹{magDetails.single_price}
                      </span>
                    </button>
                  </div>

                  {/* Add to cart */}
                  <button
                    className="
        btn btn-warning fw-semibold
        mx-auto mx-lg-0
        ms-lg-auto
      "
                    disabled={
                      !selectedPlan ||
                      selectedPlan.type === "Subscription" ||
                      (magDetails?.stock || 0) <= 0
                    }
                    onClick={handleAddToCart}
                  >
                    {Number(magDetails.stock) <= 0
                      ? "OUT OF STOCK"
                      : "ADD TO CART"}
                  </button>
                </div>
              </section>

              {/* TOAST AND APPRECIATION */}
              {showToast && (
                <div
                  style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#d4edda",
                    color: "#155724",
                    padding: "1rem 1.5rem",
                    borderRadius: "10px",
                    zIndex: 9999,
                    fontWeight: "bold",
                    fontSize: "1rem",
                    animation: "fadeInOut 3s forwards",
                    textAlign: "center",
                  }}
                >
                  {toastMessage}
                </div>
              )}
            </div>
            {/* Special Offers Marquee */}
            {/* <div
            className="special-offers mt-3 p-2 rounded"
            style={{
              background: "rgb(254, 254, 188)",
              border: "1px solid #ffe58f",
            }}
          >
            <div className="marquee-text text-dark">
              🌟 Special Offer: Good News !.Subscribe yearly and get 1 month
              FREE!. Hurry Up & Scrab This Offer Now ! & Enjoy reading 🌟
            </div>
          </div> */}
            {/* <div className="d-flex justify-content-evenly mt-3 flex-wrap text-center">
            <div className="d-flex align-items-center gap-2">
              <FaTimesCircle style={{ color: "#ff4d4f" }} />
              <p className="m-0">Cancel at Any Time</p>
            </div>

            <div className="d-flex align-items-center gap-2 mt-2 mx-2">
              <FaBolt style={{ color: "#1890ff" }} />
              <p className="m-0">Digital Subscription Instant Access</p>
            </div>

            <div className="d-flex align-items-center gap-2 mt-2 mx-2">
              <FaLock style={{ color: "#52c41a" }} />
              <p className="m-0">Verified Secure Payment</p>
            </div>
          </div> */}
          </div>

          <section className="py-4 bg-white">
            <div className="container-fluid">
              {/* Section Title */}
              <h4 className="fw-semibold mb-3 text-start">
                Magazine Description:
              </h4>

              {/* Description Content */}
              {/* Replace the static div with this dynamic one */}
              <div className="description-content">
                {magDetails.full_desc ? (
                  <div
                    style={{
                      lineHeight: "1.6",
                      color: "#444",
                      textAlign: "left",
                    }}
                    dangerouslySetInnerHTML={{ __html: magDetails.full_desc }}
                  />
                ) : (
                  <p className="text-muted italic">
                    No description available for this issue.
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* RECENT MAGAZINES */}
          <section className="py-3 w-100">
            <div className="container-fluid">
              <div className="d-flex justify-content-between mb-3">
                <h4>Recent Magazines</h4>
                <Link
                  className="viewalllinks"
                  to="/magazines"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  View All
                </Link>
              </div>

              {loadingMagazines ? (
                <div className="text-center py-4">Loading magazines...</div>
              ) : recentMagazines.length === 0 ? (
                <div className="text-center py-4">No magazines available</div>
              ) : (
                <Swiper
                  modules={[Navigation, Autoplay]}
                  navigation
                  loop={formattedMagazines.length > 6}
                  autoplay={{ delay: 2000, disableOnInteraction: false }}
                  spaceBetween={15}
                  slidesPerView={1}
                  breakpoints={{
                    375: { slidesPerView: 2 },
                    425: { slidesPerView: 2 },
                    576: { slidesPerView: 3 },
                    768: { slidesPerView: 4 },
                    1024: { slidesPerView: 5 },
                    1200: { slidesPerView: 6 },
                  }}
                >
                  {formattedMagazines.map((mag) => (
                    <SwiperSlide key={mag.product_id}>
                      <div
                        className="viewalllinks"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          navigate(
                            `/subscription/${mag.product_id}?mp_id=${mag.mp_id}&sp_id=${mag.sp_id}`,
                          );
                          window.scrollTo(0, 0);
                        }}
                      >
                        <div className="card shadow-sm h-100">
                          <img
                            src={mag.product_url}
                            className="card-img-top"
                            alt="Magazine"
                            style={{ height: "220px", objectFit: "contain" }}
                          />
                          <div className="card-body text-center">
                            <h6>{mag.formattedDate}</h6>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </section>

          {/* POPULAR CATEGORIES */}
          <section className="py-3 bg-light">
            <div className="container-fluid">
              <div className="d-flex justify-content-between mb-3">
                <h4>Magazine Categories</h4>
                <Link
                  className="viewalllinks"
                  to="/magazines"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  View All
                </Link>
              </div>

              <Swiper
                modules={[Navigation, Autoplay]}
                navigation
                // loop
                autoplay={{ delay: 2500 }}
                spaceBetween={15}
                slidesPerView={2}
                breakpoints={{
                  425: { slidesPerView: 3 },
                  576: { slidesPerView: 4 },
                  768: { slidesPerView: 5 },
                  992: { slidesPerView: 6 },
                  1200: { slidesPerView: 8 },
                }}
              >
                {categoriesLoading ? (
                  <p>Loading categories...</p>
                ) : (
                  categories.map((cat, idx) => (
                    <SwiperSlide key={idx}>
                      <div
                        className="category-box"
                        style={{ backgroundImage: `url(${cat.img})` }}
                        onClick={() => {
                          window.scrollTo(0, 0);
                          handleCategoryClick({
                            mp_id: cat.mp_id,
                            sp_id: cat.sp_id,
                          });
                        }}
                      >
                        <div className="overlay">
                          <h6>{cat.name}</h6>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))
                )}
              </Swiper>
            </div>
          </section>
        </motion.div>
      </div>
    </>
  );
}
